/**
 * The traffic-light scale every analysis score is drawn on: red for a page
 * that is losing people, orange for real problems, yellow for "works, with
 * gaps", green for strong. Colours are chart colours (a ramp inside one
 * instrument), so they live here rather than as theme tokens.
 *
 * Bands: < 40 red · 40–54 orange · 55–69 yellow · ≥ 70 green. Between the band
 * centres the colour blends, so a 54 and a 56 do not look like different worlds.
 */
type RGB = [number, number, number];

const STOPS: Array<[number, RGB]> = [
  [0, [229, 72, 77]], // red
  [35, [236, 96, 72]],
  [47, [244, 146, 64]], // orange
  [62, [238, 196, 72]], // yellow
  [74, [128, 214, 120]],
  [84, [84, 214, 152]], // green
  [100, [104, 226, 170]],
];

export function scoreRgb(value: number): RGB {
  const v = Math.max(0, Math.min(100, value));
  for (let i = 1; i < STOPS.length; i++) {
    const [b, cb] = STOPS[i];
    const [a, ca] = STOPS[i - 1];
    if (v <= b) {
      const t = (v - a) / (b - a || 1);
      return [0, 1, 2].map((k) => Math.round(ca[k] + (cb[k] - ca[k]) * t)) as RGB;
    }
  }
  return STOPS[STOPS.length - 1][1];
}

export function scoreColor(value: number, alpha = 1): string {
  const [r, g, b] = scoreRgb(value);
  return alpha >= 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export type ScoreBand = "red" | "orange" | "yellow" | "green";

export function scoreBand(value: number): ScoreBand {
  if (value >= 70) return "green";
  if (value >= 55) return "yellow";
  if (value >= 40) return "orange";
  return "red";
}

/**
 * The one wording for a score, used by every report, its PDF and the app
 * chrome, so a 62 never reads "Needs work" on screen and "Good, with gaps"
 * on paper.
 */
const BAND_LABEL: Record<ScoreBand, [string, string]> = {
  green: ["Strong", "Ισχυρό"],
  yellow: ["Good, with gaps", "Καλό, με κενά"],
  orange: ["Needs work", "Θέλει δουλειά"],
  red: ["Weak", "Αδύναμο"],
};

export function scoreBandLabel(value: number, en = true): string {
  const [a, b] = BAND_LABEL[scoreBand(value)];
  return en ? a : b;
}
